import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box, Card, Typography, Stack, TextField, FormControl, InputLabel, Select, MenuItem,
  ToggleButton, ToggleButtonGroup, Button, IconButton, Divider, Tooltip, Snackbar, Alert,
  InputAdornment
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { useTranslation } from "react-i18next";
import { PageHeader } from "../../components/PageHeader.js";
import { useApi } from "../../hooks/useApi.js";
import { formLabel } from "../../lib/labels.js";
import type { WhatsAppTemplate, PartnerReduced } from "@kappa-v2/contracts";

// Ordine tipi socio coerente col resto: Proprietario → Cavallo → Pony.
const PARTNER_TYPE_KEYS = ["form.partnerType.owner", "form.partnerType.horse", "form.partnerType.pony"];
const hasPhone = (p: PartnerReduced) => !!p.contact && p.contact.trim().startsWith("+");
const GENERIC_ATTACHMENT = "form.whatsapp.genericAttachment";

// Legge un File come data URL base64 (il backend rimuove il prefisso data:).
const fileToBase64 = (f: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = reject;
    reader.readAsDataURL(f);
  });

export function WhatsAppComposePage() {
  const { t } = useTranslation();
  const { call } = useApi();

  const [templates, setTemplates] = useState<WhatsAppTemplate[]>([]);
  const [templateId, setTemplateId] = useState("");
  const [partners, setPartners] = useState<PartnerReduced[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // Filtri lista soci
  const [partnerType, setPartnerType] = useState("");
  const [status, setStatus] = useState<"all" | "active" | "inactive">("active");
  const [search, setSearch] = useState("");

  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<{ msg: string; sev: "success" | "error" | "info" } | null>(null);
  // Allegato generico (promozionale) caricato da UI.
  const [file, setFile] = useState<File | null>(null);

  useEffect(() => {
    call<WhatsAppTemplate[]>("/api/v1/whatsapp/templates", { silent: true })
      .then((r) => setTemplates(Array.isArray(r) ? r : []))
      .catch(() => setTemplates([]));
    call<{ data?: PartnerReduced[] } | PartnerReduced[]>("/api/v1/partners/reduced?active=all", { silent: true })
      .then((r) => setPartners(Array.isArray(r) ? r : (r.data ?? [])))
      .catch(() => setPartners([]));
  }, [call]);

  const template = useMemo(() => templates.find((t) => String(t._id) === templateId), [templates, templateId]);

  // Applica i filtri (nome: startsWith, come v1).
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return partners.filter((p) => {
      if (partnerType && p.partnerType !== partnerType) return false;
      if (status === "active" && p.active === false) return false;
      if (status === "inactive" && p.active !== false) return false;
      if (q && !(p.fullName ?? "").toLowerCase().startsWith(q)) return false;
      return true;
    });
  }, [partners, partnerType, status, search]);

  const available = useMemo(() => filtered.filter((p) => !selected.has(String(p._id))), [filtered, selected]);
  const chosen = useMemo(() => filtered.filter((p) => selected.has(String(p._id))), [filtered, selected]);

  const add = (id: string) => setSelected((s) => new Set(s).add(id));
  const remove = (id: string) => setSelected((s) => { const n = new Set(s); n.delete(id); return n; });
  const addAll = () => setSelected((s) => { const n = new Set(s); available.filter(hasPhone).forEach((p) => n.add(String(p._id))); return n; });
  const removeAll = () => setSelected((s) => { const n = new Set(s); chosen.forEach((p) => n.delete(String(p._id))); return n; });

  const enqueue = useCallback(async () => {
    if (!template) { setToast({ msg: t("whatsapp.compose.noTemplate"), sev: "info" }); return; }
    const isGeneric = template.attachedType === "form.whatsapp.genericAttachment";
    if (isGeneric && !file) { setToast({ msg: t("whatsapp.compose.attachmentRequired"), sev: "info" }); return; }

    const chosenPartners = partners.filter((p) => selected.has(String(p._id)));
    if (chosenPartners.length === 0) return;
    setBusy(true);
    try {
      // Allegato generico: carico il file UNA volta su xfiles e riuso il percorso per tutti.
      let files: Array<{ url: string; fileName: string; mimeType?: string; fileType?: string }> = [];
      if (isGeneric && file) {
        const contentBase64 = await fileToBase64(file);
        const up = await call<{ url: string; fileName: string; mimeType: string }>(
          "/api/v1/whatsapp/attachments",
          { method: "POST", body: { fileName: file.name, mimeType: file.type, contentBase64 } }
        );
        files = [{ url: up.url, fileName: up.fileName, mimeType: up.mimeType, fileType: "generic" }];
      }
      // Come v1: una coda per OGNI cellulare del socio (3 numeri → 3 invii).
      const items = chosenPartners.flatMap((p) => {
        const phones = Array.from(
          new Set((p.cells?.length ? p.cells : [p.contact]).map((c) => (c ?? "").trim()).filter((c) => c.startsWith("+")))
        );
        return phones.map((cell) => ({
          partnerId: String(p._id),
          cellPhone: cell,
          cells: phones,
          fullName: p.fullName,
          message: template.message ?? "",
          attachedType: template.attachedType,
          files,
          status: "form.whatsapp.pending"
        }));
      });
      if (items.length === 0) { setBusy(false); return; }
      const r = await call<{ created: number }>("/api/v1/whatsapp/queues/multy", { method: "POST", body: { items } });
      setToast({ msg: t("whatsapp.compose.enqueued", { count: r?.created ?? items.length }), sev: "success" });
      setSelected(new Set());
      setFile(null);
    } catch { setToast({ msg: t("common.error"), sev: "error" }); }
    finally { setBusy(false); }
  }, [template, partners, selected, call, t, file]);

  const selectedCount = useMemo(() => partners.filter((p) => selected.has(String(p._id)) && hasPhone(p)).length, [partners, selected]);

  const ptName = (k: string) => formLabel(k);

  const PartnerList = ({ rows, action, onAction }: {
    rows: PartnerReduced[]; action: "add" | "remove"; onAction: (id: string) => void;
  }) => (
    <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
      {rows.map((p) => {
        const ok = hasPhone(p);
        return (
          <Box key={String(p._id)} sx={{
            display: "flex", alignItems: "center", gap: 1, px: 1.5, py: 0.75,
            borderBottom: "1px solid rgba(0,0,0,.05)", opacity: ok ? 1 : 0.5
          }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" fontWeight={600} noWrap>{p.fullName}</Typography>
              <Typography variant="caption" color={ok ? "text.secondary" : "error.main"}>
                {ok ? p.contact : t("whatsapp.compose.noPhone")}
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>{ptName(p.partnerType)}</Typography>
            <Tooltip title={action === "add" ? t("whatsapp.compose.addAll") : t("whatsapp.compose.removeAll")}>
              <span>
                <IconButton size="small" color={action === "add" ? "primary" : "error"} disabled={action === "add" && !ok} onClick={() => onAction(String(p._id))}>
                  {action === "add" ? <AddIcon fontSize="small" /> : <CloseIcon fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        );
      })}
    </Box>
  );

  return (
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0, overflow: "hidden" }}>
      <PageHeader title={t("whatsapp.compose.title")} subtitle={t("whatsapp.compose.subtitle")} />

      <Box sx={{ px: { xs: 1.5, sm: 2, md: 3 }, pt: 2, pb: 1, flex: 1, display: "flex", flexDirection: "column", minHeight: 0, gap: 2, overflowY: { xs: "auto", md: "visible" } }}>
        {/* Selezione messaggio + anteprima — fisso */}
        <Box sx={{ flexShrink: 0 }}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <FormControl size="small" sx={{ flex: 1 }} data-tour="wa-template">
              <InputLabel>{t("whatsapp.compose.messageLabel")}</InputLabel>
              <Select label={t("whatsapp.compose.messageLabel")} value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
                {templates.map((tpl) => (
                  <MenuItem key={String(tpl._id)} value={String(tpl._id)}>
                    {tpl.name} — {formLabel(tpl.attachedType)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField size="small" sx={{ width: { md: 240 } }} label={t("whatsapp.compose.attachmentType")}
              value={template ? formLabel(template.attachedType) : ""} InputProps={{ readOnly: true }} />
          </Stack>
          {template && (
            <TextField fullWidth multiline minRows={2} maxRows={5} sx={{ mt: 2 }} data-tour="wa-preview"
              label={t("whatsapp.compose.messagePreview")} value={template.message ?? ""} InputProps={{ readOnly: true }} />
          )}
          {template && template.attachedType === GENERIC_ATTACHMENT && (
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 1.5 }} flexWrap="wrap" data-tour="wa-file">
              <Button component="label" variant="outlined" size="small" startIcon={<AddIcon />}>
                {t("whatsapp.compose.chooseFile")}
                <input
                  hidden
                  type="file"
                  accept="application/pdf,image/*"
                  onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                />
              </Button>
              {file ? (
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Typography variant="body2" fontWeight={600}>{file.name}</Typography>
                  <IconButton size="small" onClick={() => setFile(null)} aria-label="remove file">
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Stack>
              ) : (
                <Typography variant="caption" color="warning.main">{t("whatsapp.compose.attachmentRequired")}</Typography>
              )}
            </Stack>
          )}
        </Box>

        {/* Azioni */}
        <Stack direction="row" spacing={1.5} sx={{ flexShrink: 0 }} alignItems="center" flexWrap="wrap" data-tour="wa-enqueue">
          <Button variant="contained" color="success" startIcon={<WhatsAppIcon />}
            disabled={busy || !template || selectedCount === 0 || (template?.attachedType === GENERIC_ATTACHMENT && !file)}
            onClick={enqueue}>
            {t("whatsapp.compose.enqueue", { count: selectedCount })}
          </Button>
          <Typography variant="caption" color="text.secondary">
            {t("whatsapp.compose.launchHint")}
          </Typography>
        </Stack>

        {/* Filtri soci */}
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ flexShrink: 0 }} alignItems={{ md: "center" }} data-tour="wa-filters">
          <ToggleButtonGroup exclusive size="small" color="primary" value={partnerType} onChange={(_, v) => v !== null && setPartnerType(v)}>
            <ToggleButton value="">{t("common.all")}</ToggleButton>
            {PARTNER_TYPE_KEYS.map((k) => <ToggleButton key={k} value={k}>{ptName(k)}</ToggleButton>)}
          </ToggleButtonGroup>
          <ToggleButtonGroup exclusive size="small" color="primary" value={status} onChange={(_, v) => v !== null && setStatus(v)}>
            <ToggleButton value="all">{t("common.all")}</ToggleButton>
            <ToggleButton value="active">{t("whatsapp.compose.active")}</ToggleButton>
            <ToggleButton value="inactive">{t("whatsapp.compose.inactive")}</ToggleButton>
          </ToggleButtonGroup>
          <TextField
            size="small"
            sx={{ width: { xs: "100%", md: 340 }, "& .MuiInputBase-root": { fontSize: "1rem" } }}
            label={t("whatsapp.compose.searchName")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start"><SearchIcon fontSize="small" sx={{ color: "text.disabled" }} /></InputAdornment>
              ),
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearch("")} edge="end" aria-label="clear">
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null
            }}
          />
        </Stack>

        {/* Due colonne disponibili / selezionati — impilate su mobile, affiancate su desktop */}
        <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 2 }} data-tour="wa-lists">
          {[{ key: "av", title: t("whatsapp.compose.available"), rows: available, action: "add" as const, onAction: add, onAll: addAll },
            { key: "ch", title: t("whatsapp.compose.selected"), rows: chosen, action: "remove" as const, onAction: remove, onAll: removeAll }].map((col) => (
            <Card key={col.key} sx={{ flex: { xs: "none", md: 1 }, height: { xs: 340, md: "auto" }, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
              <Box sx={{ px: 1.5, py: 1, bgcolor: "#F7F5F0", borderBottom: "1px solid rgba(0,0,0,.08)", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
                <Typography variant="subtitle2" fontWeight={700}>{col.title} ({col.rows.length})</Typography>
                <Button size="small" onClick={col.onAll} disabled={col.rows.length === 0}>
                  {col.action === "add" ? t("whatsapp.compose.addAll") : t("whatsapp.compose.removeAll")}
                </Button>
              </Box>
              <PartnerList rows={col.rows} action={col.action} onAction={col.onAction} />
            </Card>
          ))}
        </Box>
      </Box>

      <Snackbar open={!!toast} autoHideDuration={6000} onClose={() => setToast(null)} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        {toast ? <Alert severity={toast.sev} onClose={() => setToast(null)} variant="filled">{toast.msg}</Alert> : undefined}
      </Snackbar>
    </Box>
  );
}
